import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import FormLabel from '@material-ui/core/FormHelperText';
import Slider from '@material-ui/core/Slider';
import InputAdornment from '@material-ui/core/InputAdornment';
import { PercentageFormatter } from '../../../services/Formatters';
import Editor, { TABLE_CELL_COMPONENT, FORM_FIELD_COMPONENT, NOT_APPLICABLE } from '../Editor';
import './SliderEditor.scss';

const DEBOUNCE_DELAY = 500;

class SliderComponent extends React.PureComponent {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    value: PropTypes.number,
    editing: PropTypes.bool,
    property: PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
      digits: PropTypes.number,
      type: PropTypes.string,
      min: PropTypes.number,
      max: PropTypes.number,
      formatValue: PropTypes.func,
    }),
    label: PropTypes.string,
    error: PropTypes.string,
    readOnly: PropTypes.bool,
    form: PropTypes.bool,
  }

  static defaultProps = {
    value: 0,
    error: null,
    readOnly: false,
    label: null,
    property: {},
    editing: false,
    form: false,
  };

  constructor(props) {
    super(props);

    const { property } = props;
    this.state = {
      fractionDigits: property && property.digits || 0,
    };
    const InputProps = {
      readOnly: Boolean(props.readOnly === undefined ? !props.editing : props.readOnly),
      inputProps: { style: { textAlign: 'right' } },
      fullWidth: false,
    };
    if (property.type === 'percentage') {
      InputProps['aria-label'] = 'Percentage';
      InputProps.endAdornment = <InputAdornment position="end">%</InputAdornment>;
      const modelMin = property.min || 0;
      const modelMax = property.max || 1;
      this.factor = 100 / (modelMax - modelMin);
      this.minValue = 0;
      this.maxValue = 100;
    } else {
      if (property.formatValue) {
        const adornments = {};
        property.formatValue(props.value, null, property.name, { ...property, adornments });
        InputProps.startAdornment = adornments.prefix ? <InputAdornment position="start">{adornments.prefix}</InputAdornment> : undefined;
        InputProps.endAdornment = adornments.suffix ? <InputAdornment position="end">{adornments.suffix}</InputAdornment> : undefined;
      }
      if (property.min || property.max) {
        this.minValue = property.min === undefined ? 0 : property.min;
        this.maxValue = property.max === undefined ? 100 : property.max;
        this.factor = 100 / ((property.max === undefined ? (this.minValue + 100) : property.max) - this.minValue);
      } else {
        this.minValue = 0;
        this.factor = 1;
      }
    }
    InputProps.inputProps.min = 0;
    InputProps.inputProps.max = 100;
    this.state.InputProps = InputProps;
    this.state.value = this.modelToEditor(props.value);
    this.handleSliderChange = this.handleSliderChange.bind(this);
    this.handleTextChange = this.handleTextChange.bind(this);
    this.unmounted = false;
    this.lastValue = undefined;
  }

  componentDidUpdate(prevProps) {
    if (this.props.value !== prevProps.value) {
      this.lastValue = undefined;
      this.setState({ value: this.modelToEditor(this.props.value) });
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  handleSliderChange(event, paramValue) {
    if (!this.eventCopy) {
      this.eventCopy = Object.assign({}, event, { target: { value: 0 } });
    }
    const { fractionDigits } = this.state;
    let d = 1;
    if (fractionDigits) {
      for (let i = 1; i <= fractionDigits; i += 1) {
        d *= 10;
      }
    }
    const value = Math.round(paramValue * d) / d;
    this.setState({ value }, () => {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => {
        this.timeout = null;
        if (!this.unmounted) {
          this.eventCopy.target.value = this.editorToModel(value);
          this.props.onChange(this.eventCopy);
        }
      }, DEBOUNCE_DELAY);
    });
  }

  handleTextChange(event) {
    if (!this.eventCopy) {
      this.eventCopy = Object.assign({}, event, { target: { value: 0 } });
    }
    const { fractionDigits } = this.state;
    let value;
    let d = 1;
    if (fractionDigits) {
      for (let i = 1; i <= fractionDigits; i += 1) {
        d *= 10;
      }
      value = Math.round(parseFloat(event.target.value) * d) / d;
    } else {
      value = parseInt(event.target.value, 10);
    }
    this.setState({ value }, () => {
      this.eventCopy.target.value = this.editorToModel(this.state.value);
      this.props.onChange(this.eventCopy);
    });
  }

  editorToModel(value) {
    return this.minValue + value / this.factor;
  }

  modelToEditor(value) {
    const { fractionDigits } = this.state;
    let d = 1;
    if (fractionDigits) {
      for (let i = 1; i <= fractionDigits; i += 1) {
        d *= 10;
      }
    }
    return Math.round((value - this.minValue) * this.factor * d) / d;
  }

  render() {
    const {
      property,
      readOnly,
      editing,
      error,
      form,
    } = this.props;
    const { value, InputProps } = this.state;
    const modelValue = this.props.value;
    const edit = editing && !readOnly;
    const formattedValue = !edit && PercentageFormatter(modelValue, null, null, property);
    const label = this.props.label || property.label;
    const labelId = `LabelSlider${label}`;

    return (
      <div className={classnames('tuc-slider-editor', form && 'slider-form')}>
        {form && <Typography id={`LabelSlider${label}`}>{label}</Typography>}
        <div className="tuc-slider-field-content">
          {edit && (
            <Slider
              id={labelId}
              value={value}
              aria-labelledby={`Label${property.name}`}
              onChange={this.handleSliderChange}
            />
          )}
          {edit ? (
            <TextField
              id={`TextField${property.name}`}
              value={value}
              onChange={this.handleTextChange}
              readOnly={!editing || readOnly}
              InputProps={InputProps}
              type="number"
            />
          ) : (
            <span>{ formattedValue }</span>
          )}
        </div>
        {error && <FormLabel error>{error}</FormLabel>}
      </div>
    );
  }
}

export default class SliderEditor extends Editor {
  constructor(name, components, validation) {
    super(name, Object.assign({}, {
      [TABLE_CELL_COMPONENT]: [SliderComponent],
      [FORM_FIELD_COMPONENT]: [SliderComponent],
    }, components));
    this.validation = validation;
  }

  formatValue(value, data, propName, props) {
    return value === undefined
        ? NOT_APPLICABLE : PercentageFormatter(value, data, propName, props);
  }

  getDefaultValidators() {
    const validators = [];
    if (this.validation && this.validation.min) {
      const minValue = this.validation.min.value;
      const { message = `Entered value cannot be lower than ${minValue}` } = this.validation.min;
      validators.push((value) => {
        return value < minValue ? message : null;
      });
    }
    if (this.validation && this.validation.max) {
      const maxValue = this.validation.max.value;
      const { message = `Entered value cannot be greather than ${maxValue}` } = this.validation.max;
      validators.push((value) => {
        return value > maxValue ? message : null;
      });
    }
    return validators;
  }
}
